import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Task } from '@ml/tasks/types/classes';
import { TaskLocationService } from '@ml/tasks/services/task-location.service';
import { OrderLocation } from '@ml/orders/types/classes';
import { Subject } from 'rxjs';
import { OrderLocationsService } from '@ml/orders/services';
import { takeUntil, tap } from 'rxjs/operators';
import { TaskLocation } from '@ml/tasks/types/classes/task-location.class';
import { Location } from '@ml/locations/types';
import * as _ from 'lodash';
import { UserService } from '@ml/core/services';
import { Order } from '@ml/orders/types/order';

@Component({
  selector: 'ml-task-locations-dialog',
  template: `
    <div class="container">
      <div class="mat-title">{{ 'locations' | translate | titlecase }}</div>

      <ml-select-locations-table
        *ngIf="locations && selectedLocations"
        [locations]="locations"
        [selectedLocations]="selectedLocations"
        [disableSelect]="disableSelect"
        (addLocations)="onAddLocations($event)"
        (removeLocations)="onRemoveLocations($event)"
      ></ml-select-locations-table>

      <div
        *ngIf="loading"
        fxLayout
        fxLayoutAlign="center center"
        class="padding-16"
      >
        <mat-spinner diameter="48"></mat-spinner>
      </div>

      <div fxLayout fxLayoutAlign="end" class="margin-top-16">
        <button mat-button (click)="onClose()">
          {{ 'cancel' | translate | titlecase }}
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./task-locations-dialog.component.scss']
})
export class TaskLocationsDialogComponent implements OnInit, OnDestroy {
  orderLocations: OrderLocation[];
  taskLocations: TaskLocation[];

  locations: Location[];
  selectedLocations: number[];

  loading: boolean;
  task: Task;
  disableSelect: boolean;

  private ngUnsubscribe = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: Task,
    private userService: UserService,
    private dialogRef: MatDialogRef<TaskLocationsDialogComponent>,
    private taskLocationService: TaskLocationService,
    private orderLocationsService: OrderLocationsService
  ) {}

  ngOnInit() {
    this.task = _.cloneDeep(this.data);
    if (this.userService.isEmployee) {
      this.disableSelect = true;
    }

    const orderId = (this.task.order as Order).id as number;
    this.taskLocationService.taskId = this.task.id;
    this.loading = true;

    this.taskLocationService
      .list()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(values => {
        this.taskLocations = values;
        this.selectedLocations = this.taskLocations.map(
          value =>
            ((value.orderLocation as OrderLocation).location as Location).id
        );
      });

    this.orderLocationsService
      .subscribe$(orderId)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap(() => (this.loading = false))
      )
      .subscribe(values => {
        this.orderLocations = values;
        this.locations = this.orderLocations.map(value => {
          const locationWithStatus = value.location as Location;
          locationWithStatus.status = value.status;
          return locationWithStatus;
        });
      });
  }

  onAddLocations(locations: number[]) {
    const taskLocations = locations.map(location => {
      const orderLocationId = this.orderLocations.find(
        value => (value.location as Location).id === location
      ).id;
      const taskLocation = new TaskLocation();
      taskLocation.task = this.task.id;
      taskLocation.orderLocation = orderLocationId;
      return taskLocation;
    });
    this.taskLocationService.bulkCreate(taskLocations).subscribe(values => {
      const newLocations = values.map(value => ({
        ...value,
        orderLocation: this.orderLocations.find(
          item => item.id === (value.orderLocation as number)
        )
      }));
      this.taskLocations.push(...newLocations);
    });
  }

  onRemoveLocations(locations: number[]) {
    const taskLocations = this.taskLocations
      .filter(
        value =>
          locations.indexOf(
            ((value.orderLocation as OrderLocation).location as Location).id
          ) >= 0
      )
      .map(value => value.id);
    this.taskLocationService
      .bulkRemove(taskLocations)
      .subscribe(
        () =>
          (this.taskLocations = this.taskLocations.filter(
            value => taskLocations.indexOf(value.id) <= 0
          ))
      );
  }

  onClose() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
